import { EM_DASH } from '../../lib/typography';
import { Component } from 'preact';
import { BEVELED_BTN, GOLD_BTN, TEXT_BODY, TEXT_MUTED, TEXT_PRIMARY } from '../../lib/medievalTheme';
import { ELEMENT_ORDER } from '../../utils/sorcery/deckMetrics';
import {
  mulliganHand,
  resolveOpeningHandSizes,
  sampleOpeningHand,
} from '../../utils/sorcery/consistency';
import CardInspector from '../CardInspector';
import { InspectorHoldTracker } from '../../utils/inspectorHold';
import { pushDismiss, removeDismiss } from '../../utils/escStack';
import ElementIcon from './ElementIcon';
import HandFan from './HandFan';
import StatCard from './StatCard';
import { t, k } from '../../localization/t';

const GOOD = '#7AA06A';
const WARN = '#D9B968';
const BAD = '#C85C4A';

function reachColor(pct) {
  if (pct >= 0.8) return GOOD;
  if (pct >= 0.5) return WARN;
  return BAD;
}

function formatPercent(p) {
  if (!Number.isFinite(p)) return EM_DASH;
  return `${Math.round(p * 100)}%`;
}

function ThresholdReachTable({ reach }) {
  // Always render all four element rows so the card has a stable height.
  // Rows with no demand render in a muted style with em-dashes.
  return (
    <div>
      <div className="grid gap-y-1.5" style={{ gridTemplateColumns: '9.375cqw repeat(5, minmax(0, 1fr))' }}>
        <div />
        {[1, 2, 3, 4, 5].map((turn) => (
          <div
            key={turn}
            className="text-center text-[0.625cqw] font-semibold"
            style={{ color: TEXT_MUTED }}
          >
            {t('Turn {n}', { n: turn })}
          </div>
        ))}

        {ELEMENT_ORDER.map((el) => {
          const demand = reach.demand[el] || 0;
          const inactive = demand === 0;
          return (
            <div key={`row-${el}`} className="contents">
              <div className="flex items-center gap-2 text-sm">
                <ElementIcon element={el} size={18} muted={inactive} />
                <span
                  className="font-medium"
                  style={{ color: inactive ? TEXT_MUTED : TEXT_PRIMARY }}
                >
                  {t(el)}
                </span>
                <span className="text-xs" style={{ color: TEXT_BODY, opacity: 0.6 }}>
                  {inactive ? t('(no demand)') : (demand === 1 ? t('(1 pip)') : t('({n} pips)', { n: demand }))}
                </span>
              </div>
              {reach[el].map((p, idx) => {
                if (inactive) {
                  return (
                    <div
                      key={`${el}-${idx}`}
                      className="flex items-center justify-center py-1.5"
                      style={{ color: TEXT_MUTED, opacity: 0.4 }}
                    >
                      <span className="text-sm">{EM_DASH}</span>
                    </div>
                  );
                }
                const turn = idx + 1;
                const isTarget = turn === reach.target[el];
                const color = reachColor(p);
                const check = p >= 0.8;
                return (
                  <div
                    key={`${el}-${turn}`}
                    className="flex items-center justify-center py-1.5 rounded-md"
                    style={{
                      background: `linear-gradient(180deg, ${color}22 0%, ${color}11 100%)`,
                      border: `1px solid ${isTarget ? color : 'transparent'}`,
                      boxShadow: isTarget ? `0 0 8px ${color}33` : 'none',
                    }}
                  >
                    <span className="text-sm font-semibold tabular-nums" style={{ color }}>
                      {formatPercent(p)}
                    </span>
                    {check ? (
                      <span className="ml-1 text-xs" style={{ color }}>
                        ✓
                      </span>
                    ) : null}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      <p className="mt-4 text-[0.6875cqw] leading-4" style={{ color: TEXT_BODY, opacity: 0.8 }}>
        {t("Outlined cell marks the turn each element's heaviest threshold becomes possible. Assumes best-case play: draw atlas every turn, play a site every turn.")}
      </p>
    </div>
  );
}

const SPELL_KEY_PREFIX = 'sb:';
const ATLAS_KEY_PREFIX = 'at:';

function keyFor(entry, index) {
  return entry?.zone === 'atlas' ? `${ATLAS_KEY_PREFIX}${index}` : `${SPELL_KEY_PREFIX}${index}`;
}

function splitSelectedKeys(selected) {
  const spellIndices = [];
  const atlasIndices = [];
  for (const k of selected) {
    if (typeof k !== 'string') continue;
    const n = Number(k.slice(3));
    if (!Number.isFinite(n)) continue;
    if (k.startsWith(SPELL_KEY_PREFIX)) spellIndices.push(n);
    else if (k.startsWith(ATLAS_KEY_PREFIX)) atlasIndices.push(n);
  }
  return { spellIndices, atlasIndices };
}

const CARD_TYPE_ORDER = ['Minion', 'Magic', 'Aura', 'Artifact'];
const CARD_TYPE_PLURAL = { Minion: k('Minions'), Magic: k('Magics'), Aura: k('Auras'), Artifact: k('Artifacts'), Site: k('Sites') };

const THRESHOLD_FIELDS = ['waterThreshold', 'earthThreshold', 'fireThreshold', 'airThreshold'];

/**
 * Earliest turn a spell can enter play: bounded below by its mana cost
 * (you need N sites for N mana) AND by its heaviest threshold demand
 * (you need at least that many sites in play to supply the pips). Both
 * caps are best-case: assumes you play a site every turn from turn 1.
 */
function earliestCastTurn(card) {
  if (!card) return 1;
  const cost = Number(card.cost);
  let maxThreshold = 0;
  for (const field of THRESHOLD_FIELDS) {
    const v = Number(card[field] || 0);
    if (v > maxThreshold) maxThreshold = v;
  }
  const costFloor = Number.isFinite(cost) && cost > 0 ? cost : 1;
  return Math.max(1, costFloor, maxThreshold);
}

function buildHandInsights(hand) {
  const all = [...(hand.spellbook || []), ...(hand.atlas || [])];
  const types = { Minion: 0, Magic: 0, Aura: 0, Artifact: 0, Site: 0 };
  for (const entry of all) {
    const type = entry?.card?.type;
    if (type && Object.prototype.hasOwnProperty.call(types, type)) types[type] += 1;
  }

  const castable = all.filter((e) => {
    const t = e?.card?.type;
    return t && t !== 'Site' && t !== 'Avatar';
  });
  const byTurn = new Map();
  for (const entry of castable) {
    const turn = earliestCastTurn(entry.card);
    if (!byTurn.has(turn)) byTurn.set(turn, []);
    byTurn.get(turn).push(entry.card.name);
  }
  const timeline = [...byTurn.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([turn, names]) => ({ turn, count: names.length, names }));

  return { types, timeline, totalSpells: castable.length };
}

function buildFanEntries(hand, spellGens = [], atlasGens = []) {
  // Mark each entry with the pile it came from so mulligan clicks
  // route back to the right list, plus a per-slot generation counter
  // so only changed slots remount + replay the draw-in animation.
  const spellbookEntries = (hand.spellbook || []).map((entry, i) => ({
    ...entry,
    zone: 'spellbook',
    _fanKey: `${SPELL_KEY_PREFIX}${i}`,
    _entryGen: spellGens[i] || 0,
  }));
  const atlasEntries = (hand.atlas || []).map((entry, i) => ({
    ...entry,
    zone: 'atlas',
    _fanKey: `${ATLAS_KEY_PREFIX}${i}`,
    _entryGen: atlasGens[i] || 0,
  }));
  // One hand. This is what the player actually draws. Order by card
  // category rather than by source pile so the fan groups spells then
  // sites for every avatar: Magician folds sites into the spellbook deck
  // and has no atlas at all, so keying off the pile would scatter its
  // rotated sites through the vertical spells. Each entry keeps its
  // `_fanKey` so mulligan still resolves back to the right pile.
  const isSite = (e) => e.card?._sorceryCategory === 'Site';
  const all = [...spellbookEntries, ...atlasEntries];
  return [...all.filter((e) => !isSite(e)), ...all.filter(isSite)];
}

class OpeningHandSimulator extends Component {
  constructor(props) {
    super(props);
    const hand = sampleOpeningHand(props.chosenCards);
    this.state = {
      hand,
      selected: new Set(),
      // Per-slot generation counters. Bumping a slot's counter
      // remounts just that card in the fan (replaying the draw-in
      // animation) while leaving kept cards in place.
      spellGens: new Array(hand.spellbook?.length || 0).fill(0),
      atlasGens: new Array(hand.atlas?.length || 0).fill(0),
      hoveredEntry: null,
      inspectedEntry: null,
    };
    this.inspectorHold = new InspectorHoldTracker();
  }

  componentDidMount() {
    document.addEventListener('keydown', this.handleKeyDown);
    document.addEventListener('keyup', this.handleKeyUp);
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.handleKeyDown);
    document.removeEventListener('keyup', this.handleKeyUp);
    removeDismiss(this._escDismissId);
  }

  componentDidUpdate(prevProps, prevState) {
    // Space-was-armed → hover landed on a card → open inspector now.
    if (
      !this.state.inspectedEntry &&
      this.state.hoveredEntry &&
      this.state.hoveredEntry !== prevState.hoveredEntry &&
      this.inspectorHold.consumePending()
    ) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ inspectedEntry: this.state.hoveredEntry });
    }

    const open = !!this.state.inspectedEntry;
    const wasOpen = !!prevState.inspectedEntry;
    if (open && !wasOpen) this._escDismissId = pushDismiss(() => this.setState({ inspectedEntry: null }));
    else if (!open && wasOpen) removeDismiss(this._escDismissId);

    if (prevProps.chosenCards !== this.props.chosenCards) {
      const hand = sampleOpeningHand(this.props.chosenCards);
      this.setState((state) => ({
        hand,
        selected: new Set(),
        spellGens: (state.spellGens || []).map((g) => g + 1)
          .concat(new Array(Math.max(0, (hand.spellbook?.length || 0) - (state.spellGens?.length || 0))).fill(1))
          .slice(0, hand.spellbook?.length || 0),
        atlasGens: (state.atlasGens || []).map((g) => g + 1)
          .concat(new Array(Math.max(0, (hand.atlas?.length || 0) - (state.atlasGens?.length || 0))).fill(1))
          .slice(0, hand.atlas?.length || 0),
        hoveredEntry: null,
        inspectedEntry: null,
      }));
    }
  }

  isEditableTarget = (el) => {
    if (!el) return false;
    if (el.isContentEditable) return true;
    const tag = el.tagName?.toLowerCase();
    return tag === 'input' || tag === 'textarea' || tag === 'select';
  };

  handleKeyDown = (e) => {
    if (this.isEditableTarget(e.target)) return;

    if (e.key === ' ' || e.code === 'Space') {
      const { hoveredEntry, inspectedEntry } = this.state;
      // OS key-repeat fires while Space is held. Only act on the
      // first keydown so hold-to-inspect stays open until keyup.
      if (e.repeat) {
        if (inspectedEntry || hoveredEntry) e.preventDefault();
        return;
      }
      if (inspectedEntry) {
        e.preventDefault();
        this.inspectorHold.noteClose();
        this.setState({ inspectedEntry: null });
        return;
      }
      if (hoveredEntry) {
        e.preventDefault();
        this.inspectorHold.noteOpen();
        this.setState({ inspectedEntry: hoveredEntry });
        return;
      }
      // No card hovered yet. Arm so the next hover opens.
      e.preventDefault();
      this.inspectorHold.noteArmed();
      return;
    }
  };

  // Releasing Space closes the inspector only if it was held past the
  // hold threshold; quick taps leave it open (tap-toggle mode).
  handleKeyUp = (e) => {
    if (e.key !== ' ' && e.code !== 'Space') return;
    // Tracker first: releasing Space also disarms a hover-less press.
    if (!this.inspectorHold.shouldCloseOnKeyUp() || !this.state.inspectedEntry) return;
    e.preventDefault();
    this.setState({ inspectedEntry: null });
  };

  toggle = (key) => {
    this.setState((state) => {
      const next = new Set(state.selected);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return { selected: next };
    });
  };

  handleHover = (entry) => {
    this.setState({ hoveredEntry: entry });
  };

  handleRedraw = () => {
    const hand = sampleOpeningHand(this.props.chosenCards);
    this.setState((state) => ({
      hand,
      selected: new Set(),
      // Full redraw: every slot is new, so bump every counter.
      spellGens: new Array(hand.spellbook?.length || 0).fill(0)
        .map((_, i) => (state.spellGens[i] || 0) + 1),
      atlasGens: new Array(hand.atlas?.length || 0).fill(0)
        .map((_, i) => (state.atlasGens[i] || 0) + 1),
      hoveredEntry: null,
    }));
  };

  handleMulligan = () => {
    const { hand, selected } = this.state;
    if (selected.size === 0) return;
    const { spellIndices, atlasIndices } = splitSelectedKeys(selected);
    const next = mulliganHand(this.props.chosenCards, hand, { spellIndices, atlasIndices });
    const spellIdxSet = new Set(spellIndices);
    const atlasIdxSet = new Set(atlasIndices);
    this.setState((state) => ({
      hand: next,
      selected: new Set(),
      // Only bump the counters for positions that were actually
      // replaced. Kept slots keep their existing counter, so their
      // React key doesn't change and they don't re-animate.
      spellGens: (state.spellGens || []).map((g, i) => (spellIdxSet.has(i) ? g + 1 : g)),
      atlasGens: (state.atlasGens || []).map((g, i) => (atlasIdxSet.has(i) ? g + 1 : g)),
      hoveredEntry: null,
    }));
  };

  render() {
    const { hand, selected, spellGens, atlasGens, inspectedEntry } = this.state;
    const { chosenCards } = this.props;
    const sizes = resolveOpeningHandSizes(chosenCards);
    const canMulligan = selected.size > 0;
    const fanEntries = buildFanEntries(hand, spellGens, atlasGens);
    const insights = buildHandInsights(hand);
    const avatarCard = chosenCards?.find?.((e) => e?.card?.type === 'Avatar')?.card;
    const avatarName = avatarCard?.name;

    const totalCards = sizes.spellbook + sizes.atlas;

    // Non-standard opening (anything other than 3 + 3) gets a tag in
    // the header so an empty atlas or a 7-card spellbook reads as an
    // avatar rule rather than a bug.
    const customOpening = sizes.spellbook !== 3 || sizes.atlas !== 3;
    const avatarTag = avatarName && customOpening
      ? t("{name}'s opener", { name: avatarName })
      : null;

    return (
      <div className="flex flex-col gap-3">
        {/* Meta row. Deliberately does NOT restate "Opening hand." The
            StatCard heading two lines above already says it. This is the
            composition readout that used to sit in a mostly-empty box
            below the fan. */}
        <div className="flex items-baseline justify-between gap-4 flex-wrap">
          <div className="flex items-baseline gap-2 flex-wrap">
            <CompositionLine insights={insights} />
            {avatarTag ? (
              <span
                className="text-[0.5625cqw] px-2 py-0.5 rounded-full"
                style={{
                  color: TEXT_PRIMARY,
                  background: 'rgba(180,140,60,0.14)',
                  border: '1px solid rgba(180,140,60,0.32)',
                }}
                title={t('This avatar modifies the standard 3+3 opener')}
              >
                {avatarTag}
              </span>
            ) : null}
          </div>
          <span className="text-[0.625cqw] tabular-nums" style={{ color: TEXT_MUTED }}>
            {totalCards === 1 ? t('1 card') : t('{n} cards', { n: totalCards })}
          </span>
        </div>

        <div className="relative flex items-end justify-center" style={{ minHeight: '18.5cqw' }}>
          {/* Warm floor glow. The contact shadow that grounds the cards
              is nested with the fan below, so it tracks the card
              baseline rather than this stage's padding. */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{ background: 'radial-gradient(ellipse 58% 92% at 50% 96%, rgba(180,140,60,0.13) 0%, transparent 70%)' }}
          />

          <div className="relative pt-3">
            {/* Contact shadow at the card baseline. The warm floor glow
                alone left the fan looking pasted on; this is what sits
                it down. It paints below the cards. They're positioned
                siblings later in DOM order. */}
            <div
              className="absolute pointer-events-none"
              style={{
                left: '-4%',
                right: '-4%',
                bottom: '-0.5cqw',
                height: '2cqw',
                background: 'radial-gradient(ellipse 50% 50% at 50% 50%, rgba(0,0,0,0.55) 0%, transparent 70%)',
              }}
            />
            <HandFan
              cards={fanEntries}
              selectedKeys={selected}
              onToggle={(key) => this.toggle(key)}
              onHover={this.handleHover}
              keyFor={(entry) => entry._fanKey}
            />
          </div>
        </div>

        {/* Action bar. The count lives inside the Mulligan label at a
            fixed button width, so arming a mulligan never reflows the
            row. That's what the old separate count line was for. */}
        <div className="flex items-center justify-center gap-3 flex-wrap">
          <button
            type="button"
            onClick={this.handleMulligan}
            disabled={!canMulligan}
            className="py-2 px-4 text-[0.6875cqw] font-semibold whitespace-nowrap tabular-nums transition-transform duration-150 hover:scale-[1.02] active:scale-[0.97] disabled:pointer-events-none"
            style={{
              ...(canMulligan ? GOLD_BTN : BEVELED_BTN),
              ...(canMulligan ? null : { color: TEXT_MUTED, opacity: 0.55 }),
              width: '9.5cqw',
            }}
          >
            {canMulligan ? t('Mulligan · {n}', { n: selected.size }) : t('Mulligan')}
          </button>
          <button
            type="button"
            onClick={this.handleRedraw}
            className="py-2 px-4 text-[0.6875cqw] font-semibold whitespace-nowrap transition-transform duration-150 hover:scale-[1.02] active:scale-[0.97]"
            style={{ ...BEVELED_BTN, color: TEXT_BODY, width: '7.5cqw' }}
          >
            {t('Redraw')}
          </button>
          <span className="flex items-center gap-1.5 text-[0.625cqw] ml-1" style={{ color: TEXT_MUTED }}>
            {t('Hover a card and press')}
            <kbd
              className="inline-flex items-center px-1.5 py-0.5 rounded font-semibold not-italic"
              style={{
                fontSize: '0.5625cqw',
                color: TEXT_BODY,
                background: 'rgba(180,140,60,0.1)',
                border: '1px solid rgba(180,140,60,0.22)',
              }}
            >
              {t('Space')}
            </kbd>
            {t('to inspect')}
          </span>
        </div>

        <CastTimeline insights={insights} />

        {inspectedEntry ? (
          <CardInspector
            card={inspectedEntry.card}
            imageUrl={inspectedEntry.printing?.image_url || inspectedEntry.card?.printings?.[0]?.image_url}
            rarity={inspectedEntry.card?.rarity}
            foiling={inspectedEntry.printing?.foiling}
            onClose={() => this.setState({ inspectedEntry: null })}
          />
        ) : null}
      </div>
    );
  }
}

/** Inline composition readout. Replaces the old mostly-empty box. */
function CompositionLine({ insights }) {
  const { types } = insights;
  const parts = CARD_TYPE_ORDER
    .map((type) => ({ type, count: types[type] || 0 }))
    .filter((row) => row.count > 0);
  if (types.Site) parts.push({ type: 'Site', count: types.Site });

  if (parts.length === 0) {
    return (
      <span className="text-[0.6875cqw]" style={{ color: TEXT_MUTED }}>
        {t('No cards in this opener.')}
      </span>
    );
  }

  return (
    <span className="flex items-baseline gap-2 flex-wrap text-[0.75cqw]">
      {parts.map((part, i) => (
        <span key={part.type} className="flex items-baseline gap-1.5">
          {i > 0 ? (
            <span style={{ color: 'rgba(180,140,60,0.35)' }}>·</span>
          ) : null}
          <span className="tabular-nums font-semibold" style={{ color: TEXT_PRIMARY }}>
            {part.count}
          </span>
          <span style={{ color: TEXT_BODY }}>
            {part.count === 1 ? t(part.type) : t(CARD_TYPE_PLURAL[part.type])}
          </span>
        </span>
      ))}
    </span>
  );
}

/**
 * Earliest-cast-turn track. Full width and frameless to match the rest
 * of the dashboard; the min-height reserves two chip rows so a mulligan
 * that changes the spell count doesn't jolt the layout. The job the
 * old fixed-height box was doing.
 */
function CastTimeline({ insights }) {
  const { timeline, totalSpells } = insights;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2.5">
        <span
          className="text-[0.625cqw] font-semibold shrink-0"
          style={{ color: TEXT_MUTED }}
        >
          {t('Earliest cast turn')}
        </span>
        <span
          className="flex-1"
          style={{ height: '1px', background: 'linear-gradient(90deg, rgba(180,140,60,0.22), transparent)' }}
        />
      </div>

      <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5" style={{ minHeight: '3.25cqw' }}>
        {totalSpells === 0 ? (
          <span className="text-[0.6875cqw]" style={{ color: TEXT_BODY, opacity: 0.75 }}>
            {t('No spells in this opener.')}
          </span>
        ) : (
          timeline.map(({ turn, names }) => (
            <span
              key={turn}
              className="inline-flex items-center gap-2 rounded-full py-1 pl-1 pr-3"
              style={{ background: 'rgba(180,140,60,0.06)', border: '1px solid rgba(180,140,60,0.16)' }}
              title={t('Castable by main phase of turn {n}', { n: turn })}
            >
              <span
                className="inline-flex items-center justify-center tabular-nums font-semibold text-[0.625cqw]"
                style={{
                  minWidth: '1.75cqw',
                  height: '1.25cqw',
                  borderRadius: 'var(--radius-full)',
                  color: TEXT_PRIMARY,
                  background: 'rgba(180,140,60,0.16)',
                  border: '1px solid rgba(180,140,60,0.28)',
                }}
              >
                T{turn}
              </span>
              <span className="text-[0.6875cqw]" style={{ color: TEXT_BODY }}>
                {names.join(', ')}
              </span>
            </span>
          ))
        )}
      </div>

      <p className="text-[0.625cqw]" style={{ color: TEXT_MUTED }}>
        {t('Earliest turn a spell could be cast given its cost and threshold pips.')}
      </p>
    </div>
  );
}


export function ThresholdReachCard({ reach, className }) {
  return (
    <StatCard
      title={t('Threshold Reach')}
      description={t("By which turn can you reliably have enough affinity in play for each element's heaviest threshold?")}
      className={className}
    >
      <ThresholdReachTable reach={reach} />
    </StatCard>
  );
}

export function OpeningHandCard({ chosenCards, className }) {
  return (
    <StatCard
      title={t('Opening Hand')}
      description={t('A random opener. Click cards to mulligan them.')}
      className={className}
    >
      <OpeningHandSimulator chosenCards={chosenCards} />
    </StatCard>
  );
}
